import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useDispatch } from 'react-redux';

import { getAdminProductModelData } from '../../actions/officeAction';
import Loader from '../../components/Loader';
import PaginationComponent from '../../components/PaginationComponent';
import AdminSearchFilterPanel from '../../components/AdminSearchFilterPanel/AdminSearchFilterPanel';
import CatalogManualReviewQueueModal from './CatalogManualReviewQueueModal';
import AdminProductCompareModal from './AdminProductCompareModal';
import ModalConfirm from '../../components/BurgerMenu/ModalConfirm';
import { resolveAnyStaticUrl } from '../../urlConfig';
import {
    analyzeCatalogProductLinks,
    applyCatalogProductLinks,
    revertCatalogProductLinks,
    downloadCatalogExtensionReport,
    estimateCatalogExtension,
    listPricingProducts,
    saveCatalogExtensionGeometry,
    setCatalogReviewSkip,
    setCatalogReviewNumberExclusion,
    queueManualReviewProducts,
} from './catalogReviewApi';
import {
    disposeCatalogModelData,
    measureCatalogModelGeometry,
} from './catalogGeometryMeasurement';

const CATALOG_FROM = 1;
const CATALOG_TO = 1150;
const ALL_CATEGORIES_ID = '__all__';
const PRICING_FILTERS = new Set([
    'all',
    'unchecked',
    'checked',
    'notImported',
    'imported',
]);


const SECTIONS = [
    { value: 'catalog', label: 'Каталог и товары' },
    { value: 'geometry', label: '3D и размеры' },
    { value: 'pricing', label: 'Цены и подтверждение' },
    { value: 'issues', label: 'Проблемы и отчёт' },
];

const money = (value) => {
    const number = Number(value);
    return Number.isFinite(number) && number > 0
        ? `${number.toLocaleString('ru-RU')} ₽`
        : '—';
};

const size = (dimensions = {}) => {
    const height = Number(dimensions?.height || 0);
    const width = Number(dimensions?.width || 0);
    const thickness = Number(dimensions?.thickness || 0);

    return height > 0 && width > 0 && thickness > 0
        ? `${Math.round(height)}×${Math.round(width)}×${Math.round(thickness)} мм`
        : '—';
};

const hasDimensions = (dimensions = {}) => (
    Number(dimensions?.height || 0) > 0
    && Number(dimensions?.width || 0) > 0
    && Number(dimensions?.thickness || 0) > 0
);

const getErrorMessage = (error) => (
    error?.response?.data?.message
    || error?.message
    || 'Произошла ошибка'
);

const productName = (product) => {
    const safeProduct = product && typeof product === 'object' ? product : {};

    return (
        safeProduct.fullName
        || safeProduct.name
        || `Товар ${safeProduct.article || ''}`.trim()
        || 'Товар не найден'
    );
};

const getErrorCode = (error) => (
    error?.response?.data?.code
    || error?.code
    || ''
);

const isReviewedStatus = (status) => (
    ['approved', 'edited', 'manual', 'source-confirmed'].includes(String(status || ''))
);

const geometryStatusLabel = (status) => {
    if (status === 'measured') return 'Размер получен';
    if (status === 'pending') return 'Ожидает анализа';
    if (status === 'failed') return 'Ошибка анализа';
    if (status === 'unavailable') return 'Нет 3D';
    return 'Не проверено';
};

const reviewStatusLabel = (status) => {
    if (status === 'approved') return 'Подтверждено';
    if (status === 'edited') return 'Исправлено';
    if (status === 'manual') return 'Ручная проверка';
    if (status === 'source-confirmed') return 'Из ведомости';
    return 'Не проверено';
};


const formatPriceEditTime = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';

    return date.toLocaleString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
    });
};

const getLastPriceEditFromItem = (item = {}) => {
    if (item?.lastPriceEdit?.userName) return item.lastPriceEdit;

    const logs = Array.isArray(item?.auditLog) ? item.auditLog : [];
    const entry = [...logs].reverse().find((row) => row?.action === 'pricing-update');
    if (entry?.userName) {
        return {
            userId: entry.userId || null,
            userName: entry.userName,
            createdAt: entry.createdAt || null,
        };
    }

    if (item?.updatedByName && item?.reviewedAt) {
        return {
            userId: item.updatedBy || null,
            userName: item.updatedByName,
            createdAt: item.reviewedAt,
        };
    }

    return null;
};

const rowStateLabel = (row = {}) => {
    if (row.status === 'duplicate') return 'Дубль номера';
    if (row.status === 'ready') return 'Нужно синхронизировать';
    if (row.status === 'orphan-item') return 'Товар не найден';
    if (row.transfer?.status === 'imported') return 'Перенесено в товар';
    if (row.transfer?.status === 'changed') return 'Изменено после переноса';
    if (row.transfer?.status === 'pending') return 'Готово к переносу';
    if (row.existingItem?.catalogSource?.type === 'new-catalog-product') {
        return 'Новый каталог';
    }
    if (row.existingItem) return 'Связано';
    return 'Не обработано';
};

const CatalogLoadingState = ({
    title,
    description = '',
    compact = false,
}) => (
    <div
        className={`catalogReview__extensionLoadingState ${compact ? 'is-compact' : ''}`}
        role="status"
        aria-live="polite"
    >
        <Loader typeSize={compact ? '42px' : '68px'} />
        <div>
            <strong>{title}</strong>
            {description ? <span>{description}</span> : null}
        </div>
    </div>
);

const GalleryModal = ({ row, activeIndex: sharedActiveIndex = 0, onActiveIndexChange, onClose }) => {
    const images = Array.from(new Set([
        row?.imagePath,
        ...(row?.galleryImages || []),
        ...(row?.product?.galleryImages || []),
    ].filter(Boolean)));
    const [activeIndex, setActiveIndex] = useState(
        Math.max(0, Math.min(images.length - 1, Number(sharedActiveIndex) || 0))
    );

    useEffect(() => {
        setActiveIndex(
            Math.max(0, Math.min(images.length - 1, Number(sharedActiveIndex) || 0))
        );
    }, [images.length, sharedActiveIndex]);

    if (!row || !images.length) return null;

    return (
        <div
            className="catalogReview__extensionGalleryBackdrop"
            onMouseDown={onClose}
            role="presentation"
        >
            <div
                className="catalogReview__extensionGallery"
                data-catalog-sync-scope={`gallery-${row._scopeKey || row.purchasedNumber}`}
                onMouseDown={(event) => event.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-label={`Фотографии формы №${row.purchasedNumber}`}
            >
                <header>
                    <div>
                        <strong>Форма №{row.purchasedNumber}</strong>
                        <span>{productName(row.product)}</span>
                    </div>
                    <button type="button" onClick={onClose}>Закрыть</button>
                </header>

                <div
                    className="catalogReview__extensionGalleryMain"
                    data-catalog-scroll-id={`gallery-main-${row._scopeKey || row.purchasedNumber}`}
                >
                    <img
                        src={resolveAnyStaticUrl(images[activeIndex])}
                        alt={`Форма №${row.purchasedNumber}, вид ${activeIndex + 1}`}
                    />
                </div>

                <div
                    className="catalogReview__extensionGalleryThumbs"
                    data-catalog-scroll-id={`gallery-thumbs-${row._scopeKey || row.purchasedNumber}`}
                >
                    {images.map((image, index) => (
                        <button
                            type="button"
                            key={`${image}-${index}`}
                            className={activeIndex === index ? 'is-active' : ''}
                            onClick={() => { setActiveIndex(index); onActiveIndexChange?.(index); }}
                        >
                            <img
                                src={resolveAnyStaticUrl(image)}
                                alt={`Вид ${index + 1}`}
                            />
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
};

/** Отдельная карточка проблемного товара: никакой зависимости от номера формы. */
const REVIEW_CLASS_LABELS = {
    variant: 'Это вариант',
    part: 'Часть модели',
    handmade: 'Ручная модель',
    other: 'Пропущено',
};

const priceQueueState = (product = {}) => {
    if (product.manualReviewQueued) {
        return isReviewedStatus(product.manualReviewStatus)
            ? 'Цена подтверждена · добавлен вручную'
            : 'На проверке цены · добавлен вручную';
    }
    if (product.catalogReviewLinked) return 'Уже есть запись проверки цены';
    return product.modelPath || product.has3D
        ? 'На оценку ещё не отправлен'
        : 'Для оценки сначала нужна 3D-модель';
};

const CatalogIssueProductCard = ({
    product, note = '', actionText = '', onAction, onOpen, onQueue, busy = false,
}) => {
    const productId = String(product?._id || product?.productId || '');
    const image = product?.reducedImage || product?.image || product?.galleryImages?.[0] || '';
    const has3D = Boolean(product?.modelPath || product?.has3D);
    const alreadyQueued = product?.manualReviewQueued === true;
    return (
        <div className="catalogReview__issueProductCard">
            <button type="button" className="catalogReview__issueProductPhoto"
                onClick={() => onOpen?.(productId)} disabled={!productId}
                aria-label={`Открыть фотографии: ${productName(product)}`}>
                {image ? <img src={resolveAnyStaticUrl(image)} alt={productName(product)} loading="lazy" />
                    : <span>Нет фото</span>}
            </button>
            <div className="catalogReview__issueProductInfo">
                <strong>{productName(product)}</strong>
                <span>Артикул: {product?.article || '—'} · {has3D ? '3D есть' : 'Без 3D'}</span>
                {note ? <small>{note}</small> : null}
                {product?.catalogReviewSkip?.ignored === true ? (
                    <small className="catalogReview__classificationState">
                        ✓ {REVIEW_CLASS_LABELS[product.catalogReviewSkip.reason] || 'Пропущено'} · исключено из поиска форм
                    </small>
                ) : null}
                <small className={`catalogReview__reviewState ${alreadyQueued ? 'is-queued' : ''}`}>
                    {priceQueueState(product)}
                </small>
                {product?.description ? <code title={product.description}>{product.description}</code>
                    : <small>Описание не заполнено</small>}
            </div>
            <div className="catalogReview__issueProductActions">
                <button type="button" disabled={!productId || busy} onClick={() => onOpen?.(productId)}>
                    Фото и карточка
                </button>
                <button type="button" className="is-primary" disabled={!productId || !has3D || alreadyQueued || busy}
                    title={has3D ? 'Добавить в «Цены и подтверждение» → «Не проверены»'
                        : 'Чтобы отправить на оценку, сначала добавьте 3D-модель'}
                    onClick={() => onQueue?.([product])}>
                    {alreadyQueued ? 'Уже в проверке цен' : (has3D ? 'На проверку цены' : 'Нет 3D для оценки')}
                </button>
                {actionText && onAction ? (
                    <button type="button" disabled={busy} onClick={() => onAction(product)}>
                        {actionText}
                    </button>
                ) : null}
            </div>
        </div>
    );
};

const CatalogExtensionPanel = ({
    categories = [],
    categoryId = '',
    onRefresh,
    refreshToken = 0,
    sharedDraft = null,
    onDraftChange,
    onOpenPricing,
    onRealtimeChange,
    realtimeOperations = {},
    onRealtimeOperation,
}) => {
    const dispatch = useDispatch();
    const [workspace, setWorkspace] = useState(null);
    const [activeSection, setActiveSection] = useState('catalog');
    const [manualQueueOpen, setManualQueueOpen] = useState(false);
    const [selectedCompareIds, setSelectedCompareIds] = useState([]);
    const [compareIds, setCompareIds] = useState(null);
    const [confirmation, setConfirmation] = useState(null);
    const confirmationResolverRef = useRef(null);
    const [sourceFilter, setSourceFilter] = useState('all');
    const [issueFilter, setIssueFilter] = useState('all');
    const [geometryFilter, setGeometryFilter] = useState('unchecked');
    const [pricingFilter, setPricingFilter] = useState('unchecked');
    const [catalogSearchDraft, setCatalogSearchDraft] = useState('');
    const [catalogSearch, setCatalogSearch] = useState('');
    const [catalogSearchPanelOpen, setCatalogSearchPanelOpen] = useState(false);
    const [pageNumber, setPageNumber] = useState(0);
    const pageSize = 30;
    const [workspaceLoading, setWorkspaceLoading] = useState(Boolean(categoryId));
    const [actionLoading, setActionLoading] = useState('');
    const [singleTransferNumber, setSingleTransferNumber] = useState(null);
    const [singleRevertNumber, setSingleRevertNumber] = useState(null);
    const [message, setMessage] = useState(null);
    const [decisionNotice, setDecisionNotice] = useState(null);
    const [localClassifications, setLocalClassifications] = useState({});
    const [galleryRow, setGalleryRow] = useState(null);
    const [categoryProgress, setCategoryProgress] = useState({
        current: 0,
        total: 0,
        name: '',
    });
    const [batch, setBatch] = useState({
        running: false,
        current: 0,
        total: 0,
        success: 0,
        failed: 0,
        currentNumber: null,
    });

    const cancelBatchRef = useRef(false);
    const handledRefreshTokenRef = useRef(0);
    const lastBatchOperationAtRef = useRef(0);
    const workspaceRequestIdRef = useRef(0);
    const catalogSearchLocalEditRef = useRef(false);
    const catalogSearchSharedValueRef = useRef(null);


    // Не блокируем браузерный event loop: используем общий ModalConfirm проекта.
    // Резолвер хранится отдельно от React state (без side effects в setState updater).
    const askConfirmation = useCallback((options) => new Promise((resolve) => {
        if (confirmationResolverRef.current) confirmationResolverRef.current(false);
        confirmationResolverRef.current = resolve;
        setConfirmation(options);
    }), []);

    const finishConfirmation = useCallback((accepted) => {
        const resolve = confirmationResolverRef.current;
        confirmationResolverRef.current = null;
        setConfirmation(null);
        resolve?.(accepted);
    }, []);

    useEffect(() => () => {
        if (confirmationResolverRef.current) {
            confirmationResolverRef.current(false);
            confirmationResolverRef.current = null;
        }
    }, []);

    const isAllCategories = categoryId === ALL_CATEGORIES_ID;

    const selectedCategories = useMemo(() => (
        isAllCategories
            ? categories
            : categories.filter(
                (category) => String(category._id) === String(categoryId)
            )
    ), [categories, categoryId, isAllCategories]);

    const payload = useMemo(() => ({
        categoryId: isAllCategories ? '' : categoryId,
        fromNumber: CATALOG_FROM,
        toNumber: CATALOG_TO,
    }), [categoryId, isAllCategories]);

    const operationKey = useCallback((name) => (
        `extension:${name}:${categoryId || 'none'}`
    ), [categoryId]);

    const remoteAnalyze = realtimeOperations[operationKey('analyze')] || null;
    const remoteGeometry = realtimeOperations[operationKey('geometry')] || null;
    const remoteEstimate = realtimeOperations[operationKey('estimate')] || null;
    const remoteApply = realtimeOperations[operationKey('apply')] || null;
    const remoteReport = realtimeOperations[operationKey('report')] || null;
    const remoteBusy = Boolean(
        remoteAnalyze || remoteGeometry || remoteEstimate || remoteApply || remoteReport
    );
    const singleTransferRunning = singleTransferNumber !== null;
    const singleRevertRunning = singleRevertNumber !== null;
    const effectiveLoading = workspaceLoading
        || Boolean(actionLoading)
        || singleTransferRunning
        || singleRevertRunning
        || remoteBusy;
    const analyzeRunning = actionLoading === 'analyze' || Boolean(remoteAnalyze);
    const estimateRunning = actionLoading === 'estimate' || Boolean(remoteEstimate);
    const applyRunning = ['prepare-apply', 'apply'].includes(actionLoading) || Boolean(remoteApply);
    const reportRunning = actionLoading === 'report' || Boolean(remoteReport);
    const reviewQueueLoading = actionLoading === 'prepare-review';
    const geometryQueueLoading = actionLoading === 'prepare-geometry';
    const effectiveCategoryProgress = categoryProgress.total > 0
        ? categoryProgress
        : {
            current: Number(remoteAnalyze?.current || 0),
            total: Number(remoteAnalyze?.total || 0),
            name: String(remoteAnalyze?.categoryName || ''),
        };
    const effectiveBatch = batch.running || !remoteGeometry
        ? batch
        : {
            running: true,
            current: Number(remoteGeometry.current || 0),
            total: Number(remoteGeometry.total || 0),
            success: Number(remoteGeometry.success || 0),
            failed: Number(remoteGeometry.failed || 0),
            currentNumber: remoteGeometry.currentNumber || null,
        };

    useEffect(() => {
        const section = sharedDraft?.catalogWorkspaceTab;
        if (SECTIONS.some((row) => row.value === section)) {
            setActiveSection(section);
        }

        const nextGeometryFilter = sharedDraft?.catalogGeometryFilter;
        if (['all', 'unchecked', 'measured', 'failed', 'without3D'].includes(nextGeometryFilter)) {
            setGeometryFilter(nextGeometryFilter);
        }

        const nextPricingFilter = sharedDraft?.catalogPricingFilter;
        if (PRICING_FILTERS.has(String(nextPricingFilter || ''))) {
            setPricingFilter(nextPricingFilter);
        }

        if (typeof sharedDraft?.catalogSearchPanelOpen === 'boolean') {
            setCatalogSearchPanelOpen(sharedDraft.catalogSearchPanelOpen);
        }

        if (typeof sharedDraft?.catalogSearch === 'string') {
            const nextSearch = String(sharedDraft.catalogSearch || '');

            /*
             * Hydration из authoritative workspace не является локальным
             * вводом. Иначе debounce отправит remote search обратно в socket
             * и два клиента начнут подтверждать друг друга по кругу.
             */
            if (catalogSearchSharedValueRef.current !== nextSearch) {
                catalogSearchSharedValueRef.current = nextSearch;
                catalogSearchLocalEditRef.current = false;
                setCatalogSearchDraft(nextSearch);
                setCatalogSearch(nextSearch.trim());
            }
        }

        if (Number.isFinite(Number(sharedDraft?.catalogPageNumber))) {
            const nextPage = Math.max(
                0,
                Math.floor(Number(sharedDraft.catalogPageNumber) || 0)
            );

            setPageNumber((previous) => (
                previous === nextPage ? previous : nextPage
            ));
        }
    }, [
        sharedDraft?.catalogWorkspaceTab,
        sharedDraft?.catalogGeometryFilter,
        sharedDraft?.catalogPricingFilter,
        sharedDraft?.catalogSearchPanelOpen,
        sharedDraft?.catalogSearch,
        sharedDraft?.catalogPageNumber,
    ]);

    useEffect(() => {
        const timer = window.setTimeout(() => {
            const nextSearch = String(catalogSearchDraft || '').trim();
            setCatalogSearch(nextSearch);

            if (!catalogSearchLocalEditRef.current) return;

            catalogSearchLocalEditRef.current = false;
            catalogSearchSharedValueRef.current = nextSearch;
            setPageNumber(0);

            onDraftChange?.({
                catalogSearch: nextSearch,
                catalogPageNumber: 0,
            });
        }, 280);

        return () => window.clearTimeout(timer);
    }, [
        catalogSearchDraft,
        onDraftChange,
    ]);

    const changeCatalogSearchDraft = useCallback((value) => {
        catalogSearchLocalEditRef.current = true;
        setCatalogSearchDraft(value);
    }, []);

    const changeCatalogSearchPanelOpen = useCallback((nextOpen) => {
        const normalizedOpen = nextOpen === true;

        setCatalogSearchPanelOpen(normalizedOpen);
        onDraftChange?.({
            catalogSearchPanelOpen: normalizedOpen,
        });
    }, [onDraftChange]);

    const showMessage = useCallback((type, text) => {
        setMessage({ type, text });
    }, []);

    const buildWorkspaceParams = useCallback((overrides = {}) => ({
        mode: 'catalog',
        page: overrides.page ?? pageNumber,
        limit: overrides.limit ?? pageSize,
        categoryId: isAllCategories ? '' : categoryId,
        categoryIds: isAllCategories
            ? selectedCategories.map((category) => category._id).join(',')
            : '',
        section: overrides.section || activeSection,
        geometryFilter: overrides.geometryFilter || geometryFilter,
        pricingFilter: overrides.pricingFilter || pricingFilter,
        sourceFilter: overrides.sourceFilter || sourceFilter,
        issueFilter: overrides.issueFilter || issueFilter,
        search: overrides.search ?? catalogSearch,
        forAction: overrides.forAction === true,
    }), [
        activeSection,
        catalogSearch,
        categoryId,
        geometryFilter,
        isAllCategories,
        pageNumber,
        pageSize,
        pricingFilter,
        sourceFilter,
        issueFilter,
        selectedCategories,
    ]);

    const loadWorkspace = useCallback(async ({ silent = false } = {}) => {
        if (!categoryId) {
            setWorkspace(null);
            return null;
        }

        const requestId = workspaceRequestIdRef.current + 1;
        workspaceRequestIdRef.current = requestId;
        if (!silent) setWorkspaceLoading(true);

        try {
            const result = await listPricingProducts(buildWorkspaceParams());
            if (workspaceRequestIdRef.current !== requestId) return null;
            setWorkspace(result);
            return result;
        } catch (error) {
            if (workspaceRequestIdRef.current !== requestId) return null;
            if (!silent) showMessage('error', getErrorMessage(error));
            return null;
        } finally {
            /*
             * Важный race-fix: silent refresh тоже может стать последним
             * запросом и тем самым отменить предыдущий обычный запрос.
             * Раньше обычный запрос уже не мог снять workspaceLoading, а
             * silent-запрос принципиально его не снимал — overlay зависал.
             * Последний актуальный запрос всегда обязан закрыть loader.
             */
            if (workspaceRequestIdRef.current === requestId) {
                setWorkspaceLoading(false);
            }
        }
    }, [buildWorkspaceParams, categoryId, showMessage]);

    const loadAllRowsForAction = useCallback(async (overrides = {}) => {
        const collected = [];
        let page = 0;
        let totalPages = 1;

        do {
            const result = await listPricingProducts(buildWorkspaceParams({
                ...overrides,
                page,
                limit: 100,
                forAction: true,
            }));
            collected.push(...(Array.isArray(result?.rows) ? result.rows : []));
            totalPages = Math.max(1, Number(result?.pagination?.totalPages || 1));
            page += 1;
        } while (page < totalPages);

        return collected;
    }, [buildWorkspaceParams]);

    useEffect(() => {
        if (!categoryId) {
            setWorkspace(null);
            setWorkspaceLoading(false);
            return;
        }

        setWorkspaceLoading(true);
        loadWorkspace().catch(() => {});
    }, [categoryId, loadWorkspace]);

    useEffect(() => {
        const totalPages = Math.max(1, Number(workspace?.pagination?.totalPages || 1));
        if (pageNumber > totalPages - 1) {
            setPageNumber(Math.max(0, totalPages - 1));
        }
    }, [pageNumber, workspace?.pagination?.totalPages]);

    useEffect(() => {
        if (
            !refreshToken
            || refreshToken === handledRefreshTokenRef.current
            || effectiveBatch.running
            || !categoryId
        ) {
            return;
        }

        handledRefreshTokenRef.current = refreshToken;
        loadWorkspace().catch(() => {});
    }, [
        refreshToken,
        effectiveBatch.running,
        categoryId,
        loadWorkspace,
    ]);

    const analyzeAll = useCallback(async () => {
        if (!categoryId) {
            showMessage('error', 'Выбери категорию товаров.');
            return;
        }

        const targets = isAllCategories ? selectedCategories : selectedCategories.slice(0, 1);
        if (!targets.length) {
            showMessage('error', 'В выбранном наборе нет категорий.');
            return;
        }

        const realtimeKey = operationKey('analyze');
        setActionLoading('analyze');
        setMessage(null);
        setCategoryProgress({ current: 0, total: targets.length, name: '' });
        onRealtimeOperation?.({ key: realtimeKey, running: true, total: targets.length });

        let success = 0;
        let failed = 0;

        try {
            for (let index = 0; index < targets.length; index += 1) {
                const category = targets[index];
                setCategoryProgress({
                    current: index + 1,
                    total: targets.length,
                    name: category.name || '',
                });
                onRealtimeOperation?.({
                    key: realtimeKey,
                    running: true,
                    current: index + 1,
                    total: targets.length,
                    categoryName: category.name || '',
                });

                try {
                    await analyzeCatalogProductLinks({ categoryId: category._id });
                    success += 1;
                } catch (error) {
                    failed += 1;
                    console.warn(
                        `[CatalogReview][analyze][${category.name || category._id}]`,
                        getErrorMessage(error)
                    );
                }
            }

            const result = await loadWorkspace({ silent: true });
            showMessage(
                failed ? 'warning' : 'success',
                isAllCategories
                    ? `Категории обработаны: ${success}. Ошибок: ${failed}.`
                    : (result?.message || 'Каталог товаров синхронизирован')
            );
            await onRefresh?.(result?.analysis || null);
            await onRealtimeChange?.({
                kind: 'catalog-analysis',
                categoryId: isAllCategories ? '' : categoryId,
                categoryIds: targets.map((category) => category._id),
            });
        } catch (error) {
            showMessage('error', getErrorMessage(error));
        } finally {
            setActionLoading('');
            setCategoryProgress({ current: 0, total: 0, name: '' });
            onRealtimeOperation?.({ key: realtimeKey, running: false });
        }
    }, [
        categoryId,
        isAllCategories,
        loadWorkspace,
        onRealtimeChange,
        onRealtimeOperation,
        onRefresh,
        operationKey,
        selectedCategories,
        showMessage,
    ]);

    const rows = workspace?.rows || [];

    useEffect(() => {
        const scopeKey = String(sharedDraft?.galleryScopeKey || '');
        const purchasedNumber = Number(sharedDraft?.galleryPurchasedNumber || 0);

        if (!scopeKey && !purchasedNumber) {
            setGalleryRow(null);
            return;
        }

        const row = rows.find((candidate) => (
            scopeKey
                ? String(candidate._scopeKey || '') === scopeKey
                : Number(candidate.purchasedNumber) === purchasedNumber
        ));

        setGalleryRow(row || null);
    }, [
        rows,
        sharedDraft?.galleryPurchasedNumber,
        sharedDraft?.galleryScopeKey,
    ]);

    const openGallery = useCallback((row) => {
        if (!row) return;
        setGalleryRow(row);
        onDraftChange?.({
            galleryPurchasedNumber: row.purchasedNumber,
            galleryScopeKey: row._scopeKey || '',
            galleryImageIndex: 0,
        });
    }, [onDraftChange]);

    const closeGallery = useCallback(() => {
        setGalleryRow(null);
        onDraftChange?.({
            galleryPurchasedNumber: null,
            galleryScopeKey: '',
            galleryImageIndex: 0,
        });
    }, [onDraftChange]);

    const catalogRows = useMemo(() => (
        rows
            .filter((row) => row.hasCurrentMatch || row.status === 'duplicate')
            .sort((left, right) => (
                Number(left.purchasedNumber) - Number(right.purchasedNumber)
            ))
    ), [rows]);

    const linkedRows = useMemo(() => (
        catalogRows.filter((row) => (
            row.status !== 'duplicate'
            && row.hasCurrentMatch
        ))
    ), [catalogRows]);

    const geometryRows = useMemo(() => (
        [...linkedRows].sort((left, right) => {
            const rank = (row) => {
                if (row.geometryStatus === 'failed') return 0;
                if (row.geometryStatus === 'pending') return 1;
                if (!row.has3D) return 2;
                if (row.geometryStatus === 'measured') return 4;
                return 3;
            };
            return rank(left) - rank(right)
                || Number(left.purchasedNumber) - Number(right.purchasedNumber);
        })
    ), [linkedRows]);

    const pricingRows = useMemo(() => (
        linkedRows.filter((row) => row.existingItem && row.has3D)
    ), [linkedRows]);

    const issueRows = useMemo(() => (
        rows.filter((row) => (
            row.status === 'duplicate'
            || row.status === 'orphan-item'
            || row.geometryStatus === 'failed'
            || !row.has3D
        ))
    ), [rows]);

    const pendingGeometryRows = useMemo(() => (
        geometryRows.filter((row) => (
            row.product?._id
            && row.product?.modelPath
            && row.existingItem
            && !['measured', 'failed', 'unavailable'].includes(row.geometryStatus)
        ))
    ), [geometryRows]);

    const failedGeometryRows = useMemo(() => (
        geometryRows.filter((row) => (
            row.product?._id
            && row.product?.modelPath
            && row.existingItem
            && row.geometryStatus === 'failed'
        ))
    ), [geometryRows]);

    const measuredGeometryRows = useMemo(() => (
        geometryRows.filter((row) => row.geometryStatus === 'measured')
    ), [geometryRows]);

    const without3DRows = useMemo(() => (
        geometryRows.filter((row) => !row.has3D)
    ), [geometryRows]);

    const uncheckedPricingRows = useMemo(() => (
        pricingRows.filter((row) => !isReviewedStatus(row.existingItem?.reviewStatus))
    ), [pricingRows]);

    const checkedPricingRows = useMemo(() => (
        pricingRows.filter((row) => isReviewedStatus(row.existingItem?.reviewStatus))
    ), [pricingRows]);

    // Сервер уже применил pricingFilter к текущей странице.
    // Повторная фильтрация по transfer скрывала строки, хотя pagination.total
    // корректно показывал найденные позиции.
    const notImportedPricingRows = useMemo(() => pricingRows, [pricingRows]);

    const importedPricingRows = useMemo(() => pricingRows, [pricingRows]);

    const visibleRows = useMemo(() => {
        if (activeSection === 'geometry') {
            if (geometryFilter === 'unchecked') return pendingGeometryRows;
            if (geometryFilter === 'measured') return measuredGeometryRows;
            if (geometryFilter === 'failed') return failedGeometryRows;
            if (geometryFilter === 'without3D') return rows.filter((row) => (
                (row.status === 'duplicate'
                    ? (row.products || []).some((product) => product.has3D !== true)
                    : row.hasCurrentMatch && !row.has3D)
            ));
            return geometryRows;
        }

        if (activeSection === 'pricing') {
            if (pricingFilter === 'unchecked') return uncheckedPricingRows;
            if (pricingFilter === 'checked') return checkedPricingRows;
            if (pricingFilter === 'notImported') return notImportedPricingRows;
            if (pricingFilter === 'imported') return importedPricingRows;
            return pricingRows;
        }

        if (activeSection === 'issues') return issueRows;
        return catalogRows;
    }, [
        activeSection,
        geometryFilter,
        pricingFilter,
        catalogRows,
        geometryRows,
        pendingGeometryRows,
        measuredGeometryRows,
        failedGeometryRows,
        without3DRows,
        pricingRows,
        uncheckedPricingRows,
        checkedPricingRows,
        notImportedPricingRows,
        importedPricingRows,
        issueRows,
    ]);

    const stopBatch = useCallback(() => {
        cancelBatchRef.current = true;
    }, []);

    const run3DBatch = useCallback(async (sourceRows, mode = 'pending') => {
        const queue = Array.isArray(sourceRows) ? [...sourceRows] : [];

        if (!queue.length) {
            showMessage(
                'success',
                mode === 'failed'
                    ? 'Ошибок для повторной проверки нет.'
                    : 'Для всех доступных 3D-моделей размер уже получен.'
            );
            return;
        }

        const realtimeKey = operationKey('geometry');
        cancelBatchRef.current = false;
        lastBatchOperationAtRef.current = 0;
        setMessage(null);
        setBatch({
            running: true,
            current: 0,
            total: queue.length,
            success: 0,
            failed: 0,
            currentNumber: null,
        });
        onRealtimeOperation?.({
            key: realtimeKey,
            running: true,
            current: 0,
            total: queue.length,
            success: 0,
            failed: 0,
            currentNumber: null,
            progress: 0,
        });

        let success = 0;
        let failed = 0;

        try {
            for (let index = 0; index < queue.length; index += 1) {
                if (cancelBatchRef.current) break;

                const row = queue[index];
                const product = row?.product;

                setBatch((previous) => ({
                    ...previous,
                    current: index + 1,
                    currentNumber: row?.purchasedNumber || null,
                    success,
                    failed,
                }));

                const operationNow = Date.now();
                if (
                    operationNow - lastBatchOperationAtRef.current >= 250
                    || index === queue.length - 1
                ) {
                    lastBatchOperationAtRef.current = operationNow;
                    onRealtimeOperation?.({
                        key: realtimeKey,
                        running: true,
                        current: index + 1,
                        total: queue.length,
                        success,
                        failed,
                        currentNumber: row?.purchasedNumber || null,
                        progress: ((index + 1) / Math.max(1, queue.length)) * 100,
                    });
                }

                if (!row?.purchasedNumber || !product?._id) {
                    failed += 1;
                    continue;
                }

                let loadedModelData = null;

                try {
                    const action = await dispatch(
                        getAdminProductModelData(product, { cache: false })
                    );
                    const modelData = action?.payload;
                    loadedModelData = modelData;

                    const measurement = measureCatalogModelGeometry({
                        modelData,
                        productDimensions: product?.dimensions || {},
                    });

                    await saveCatalogExtensionGeometry({
                        purchasedNumber: row.purchasedNumber,
                        productId: product._id,
                        measurement,
                        applyToPricing: true,
                    });

                    success += 1;
                } catch (error) {
                    failed += 1;

                    const errorCode = getErrorCode(error);
                    const rawText = getErrorMessage(error);
                    const errorText = errorCode === 'PRODUCT_MODEL_FILE_NOT_FOUND'
                        ? 'В карточке указан modelPath, но файл 3D-модели отсутствует на сервере'
                        : (
                            errorCode === 'PRODUCT_MODEL_NOT_CONFIGURED'
                                ? 'У товара не указана 3D-модель'
                                : rawText
                        );

                    console.warn(
                        `[CatalogReview][3D][${row.purchasedNumber}]`,
                        errorCode || 'MODEL_ANALYSIS_ERROR',
                        errorText
                    );

                    try {
                        await saveCatalogExtensionGeometry({
                            purchasedNumber: row.purchasedNumber,
                            productId: product._id,
                            measurement: {
                                status: 'failed',
                                productDimensions: product?.dimensions || {},
                                warnings: [
                                    errorCode
                                        ? `${errorCode}: ${errorText}`
                                        : errorText,
                                ],
                            },
                            applyToPricing: false,
                        });
                    } catch (saveError) {
                        console.warn(
                            `[CatalogReview][3D][${row.purchasedNumber}][save]`,
                            getErrorMessage(saveError)
                        );
                    }
                } finally {
                    disposeCatalogModelData(loadedModelData);
                }

                setBatch((previous) => ({
                    ...previous,
                    success,
                    failed,
                }));

                await new Promise((resolve) => window.setTimeout(resolve, 35));
            }
        } finally {
            setBatch((previous) => ({
                ...previous,
                running: false,
                currentNumber: null,
                success,
                failed,
            }));

            showMessage(
                failed ? 'warning' : 'success',
                cancelBatchRef.current
                    ? `Анализ остановлен. Получено размеров: ${success}, ошибок: ${failed}.`
                    : `Анализ завершён. Получено размеров: ${success}, ошибок: ${failed}.`
            );

            await loadWorkspace({ silent: true });
            await onRefresh?.();
            await onRealtimeChange?.({
                kind: 'geometry-analysis',
                categoryId,
            });
            onRealtimeOperation?.({
                key: realtimeKey,
                running: false,
                current: queue.length,
                total: queue.length,
                success,
                failed,
                progress: 100,
            });
        }
    }, [
        categoryId,
        dispatch,
        loadWorkspace,
        onRealtimeChange,
        onRealtimeOperation,
        onRefresh,
        operationKey,
        showMessage,
    ]);

    const analyze3D = useCallback(async () => {
        setActionLoading('prepare-geometry');
        setMessage(null);

        try {
            const queue = await loadAllRowsForAction({
                section: 'geometry',
                geometryFilter: 'unchecked',
            });
            setActionLoading('');
            return run3DBatch(queue, 'pending');
        } catch (error) {
            showMessage('error', getErrorMessage(error));
            return null;
        } finally {
            setActionLoading('');
        }
    }, [loadAllRowsForAction, run3DBatch, showMessage]);

    const retryFailed3D = useCallback(async () => {
        setActionLoading('prepare-geometry');
        setMessage(null);

        try {
            const queue = await loadAllRowsForAction({
                section: 'geometry',
                geometryFilter: 'failed',
            });
            setActionLoading('');
            return run3DBatch(queue, 'failed');
        } catch (error) {
            showMessage('error', getErrorMessage(error));
            return null;
        } finally {
            setActionLoading('');
        }
    }, [loadAllRowsForAction, run3DBatch, showMessage]);

    const recalculatePrices = useCallback(async () => {
        const realtimeKey = operationKey('estimate');
        setActionLoading('estimate');
        setMessage(null);
        onRealtimeOperation?.({ key: realtimeKey, running: true });

        try {
            const result = await estimateCatalogExtension(payload);
            showMessage(
                'success',
                result.message || `Рассчитано цен: ${result.estimatedCount}`
            );
            await loadWorkspace({ silent: true });
            await onRefresh?.();
            await onRealtimeChange?.({
                kind: 'price-estimate',
                categoryId,
            });
        } catch (error) {
            showMessage('error', getErrorMessage(error));
        } finally {
            setActionLoading('');
            onRealtimeOperation?.({ key: realtimeKey, running: false });
        }
    }, [
        categoryId,
        payload,
        onRealtimeOperation,
        operationKey,
        loadWorkspace,
        onRealtimeChange,
        onRefresh,
        showMessage,
    ]);

    const applyReadyPrices = useCallback(async () => {
        setActionLoading('prepare-apply');
        setMessage(null);

        let allReadyRows;
        try {
            allReadyRows = await loadAllRowsForAction({
                section: 'pricing',
                pricingFilter: 'notImported',
            });
        } catch (error) {
            showMessage('error', getErrorMessage(error));
            setActionLoading('');
            return;
        }

        // pricingFilter=notImported уже отфильтрован backend.
        // В товар переносим подтверждённый базовый размер формы и цену
        // фрезеровки; backend сам синхронизирует v11 shared sizes/варианты.
        const mappings = allReadyRows
            .filter((row) => (
                row.product?._id
                && row.existingItem?.readyForAdminImport === true
            ))
            .map((row) => ({
                productId: row.product._id,
                purchasedNumber: row.purchasedNumber,
            }));

        if (!mappings.length) {
            showMessage(
                'warning',
                'Нет подтверждённых размеров и цен для переноса в товары.'
            );
            setActionLoading('');
            return;
        }

        if (!await askConfirmation({
            title: 'Перенести подтверждённые цены?',
            description: `Перенести базовый размер и цену фрезеровки в ${mappings.length} товаров?`,
            confirmText: 'Перенести',
            confirmVariant: 'primary',
        })) {
            setActionLoading('');
            return;
        }

        const realtimeKey = operationKey('apply');
        setActionLoading('apply');
        setMessage(null);
        onRealtimeOperation?.({ key: realtimeKey, running: true });

        try {
            const result = await applyCatalogProductLinks(mappings);
            const rows = Array.isArray(result?.results)
                ? result.results
                : [];
            const failedRows = rows.filter(
                (row) => row?.ok !== true && row?.skipped !== true
            );
            const skippedRows = rows.filter((row) => row?.skipped === true);
            const problemRows = [...failedRows, ...skippedRows];
            const details = problemRows
                .slice(0, 4)
                .map((row) => (
                    `№${row.purchasedNumber || '—'}: ${row.error || 'ошибка переноса'}`
                ))
                .join(' · ');
            const messageText = [
                result?.message || 'Перенос завершён.',
                details,
                problemRows.length > 4
                    ? `ещё сообщений: ${problemRows.length - 4}`
                    : '',
            ].filter(Boolean).join(' · ');

            showMessage(
                failedRows.length || skippedRows.length
                    ? 'warning'
                    : 'success',
                messageText
            );

            await loadWorkspace({ silent: true });
            await onRefresh?.();

            const appliedNumbers = rows.length
                ? rows
                    .filter((row) => row?.ok === true)
                    .map((row) => Number(row.purchasedNumber || 0))
                    .filter(Boolean)
                : mappings.map((row) => row.purchasedNumber);

            if (appliedNumbers.length) {
                await onRealtimeChange?.({
                    kind: 'prices-applied',
                    categoryId,
                    purchasedNumbers: appliedNumbers,
                });
            }
        } catch (error) {
            showMessage('error', getErrorMessage(error));
        } finally {
            setActionLoading('');
            onRealtimeOperation?.({ key: realtimeKey, running: false });
        }
    }, [
        askConfirmation,
        categoryId,
        loadAllRowsForAction,
        onRealtimeOperation,
        operationKey,
        loadWorkspace,
        onRealtimeChange,
        onRefresh,
        showMessage,
    ]);

    const applySinglePrice = useCallback(async (row) => {
        const purchasedNumber = Number(row?.purchasedNumber || 0);
        const productId = String(row?.product?._id || '');
        const readyForTransfer = row?.existingItem?.readyForAdminImport === true;

        if (!purchasedNumber || !productId || !readyForTransfer) {
            showMessage('warning', 'Эта строка сейчас не находится в очереди переноса.');
            return;
        }

        const realtimeKey = operationKey('apply');
        setSingleTransferNumber(purchasedNumber);
        setMessage(null);
        onRealtimeOperation?.({ key: realtimeKey, running: true });

        try {
            const result = await applyCatalogProductLinks([{
                productId,
                purchasedNumber,
            }]);
            const resultRow = Array.isArray(result?.results)
                ? result.results[0]
                : null;

            if (resultRow?.ok === true) {
                showMessage(
                    'success',
                    `Форма №${purchasedNumber} перенесена в товар.`
                );
            } else {
                showMessage(
                    resultRow?.skipped === true ? 'warning' : 'error',
                    `№${purchasedNumber}: ${resultRow?.error || result?.message || 'Не удалось перенести'}`
                );
            }

            await loadWorkspace({ silent: true });
            await onRefresh?.();

            if (resultRow?.ok === true) {
                await onRealtimeChange?.({
                    kind: 'prices-applied',
                    categoryId,
                    purchasedNumbers: [purchasedNumber],
                });
            }
        } catch (error) {
            showMessage('error', getErrorMessage(error));
        } finally {
            setSingleTransferNumber(null);
            onRealtimeOperation?.({ key: realtimeKey, running: false });
        }
    }, [
        categoryId,
        loadWorkspace,
        onRealtimeChange,
        onRealtimeOperation,
        onRefresh,
        operationKey,
        showMessage,
    ]);

    const revertSinglePrice = useCallback(async (row) => {
        const purchasedNumber = Number(row?.purchasedNumber || 0);
        const productId = String(row?.product?._id || '');
        const imported = row?.transfer?.status === 'imported';

        if (!purchasedNumber || !productId || !imported) {
            showMessage('warning', 'Для этой строки сейчас нечего возвращать назад.');
            return;
        }

        if (!await askConfirmation({
            title: 'Отменить перенос цены?',
            description: `Убрать перенесённую фрезеровку из товара формы №${purchasedNumber}? `
                + 'Проверенная цена останется в каталоге и вернётся в очередь переноса.',
            confirmText: 'Вернуть цену',
            confirmVariant: 'warning',
        })) {
            return;
        }

        const realtimeKey = operationKey('apply');
        setSingleRevertNumber(purchasedNumber);
        setMessage(null);
        onRealtimeOperation?.({ key: realtimeKey, running: true });

        try {
            const result = await revertCatalogProductLinks([{
                productId,
                purchasedNumber,
            }]);
            const resultRow = Array.isArray(result?.results)
                ? result.results[0]
                : null;

            if (resultRow?.ok === true) {
                showMessage(
                    'success',
                    `Форма №${purchasedNumber}: перенос отменён, цена снова готова к переносу.`
                );
            } else {
                showMessage(
                    resultRow?.skipped === true ? 'warning' : 'error',
                    `№${purchasedNumber}: ${resultRow?.error || result?.message || 'Не удалось вернуть цену'}`
                );
            }

            await loadWorkspace({ silent: true });
            await onRefresh?.();

            if (resultRow?.ok === true) {
                await onRealtimeChange?.({
                    kind: 'prices-reverted',
                    categoryId,
                    purchasedNumbers: [purchasedNumber],
                });
            }
        } catch (error) {
            showMessage('error', getErrorMessage(error));
        } finally {
            setSingleRevertNumber(null);
            onRealtimeOperation?.({ key: realtimeKey, running: false });
        }
    }, [
        askConfirmation,
        categoryId,
        loadWorkspace,
        onRealtimeChange,
        onRealtimeOperation,
        onRefresh,
        operationKey,
        showMessage,
    ]);

    const downloadReport = useCallback(async () => {
        const realtimeKey = operationKey('report');
        setActionLoading('report');
        setMessage(null);
        onRealtimeOperation?.({ key: realtimeKey, running: true });

        try {
            const blob = await downloadCatalogExtensionReport(payload);
            const url = window.URL.createObjectURL(blob);
            const anchor = document.createElement('a');

            anchor.href = url;
            anchor.download = `catalog-products-${CATALOG_FROM}-${CATALOG_TO}.csv`;
            document.body.appendChild(anchor);
            anchor.click();
            anchor.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            showMessage('error', getErrorMessage(error));
        } finally {
            setActionLoading('');
            onRealtimeOperation?.({ key: realtimeKey, running: false });
        }
    }, [payload, showMessage, onRealtimeOperation, operationKey]);

    const summary = workspace?.summary || {};
    const summaryGroups = [
        {
            key: 'catalog',
            label: 'Каталог',
            value: `${summary.productsScanned || 0} товаров`,
            details: [
                `${summary.foundNumbers || 0} с номером`,
                `${summary.linkedItems || 0} связано`,
            ],
        },
        {
            key: 'geometry',
            label: '3D и размеры',
            value: `${summary.with3D || 0} моделей`,
            details: [
                `${summary.geometryMeasured || 0} измерено`,
                `${Number(summary.geometryPending || 0)} ожидают`,
            ],
        },
        {
            key: 'pricing',
            label: 'Цены',
            value: `${summary.priceEstimated || 0} заполнено`,
            details: [
                `${summary.readyForImport || 0} к переносу`,
                `${summary.importedCount || 0} перенесено`,
            ],
        },
        {
            key: 'issues',
            label: 'Проблемы',
            value: String(summary.problemCount || 0),
            details: [
                `${summary.withoutNumber || 0} без номера`,
                `${summary.duplicateNumbers || 0} дублей`,
            ],
        },
    ];

    const onManualQueued = async (result = {}) => {
        const count = Number(result.addedCount || 0);
        setDecisionNotice({
            type: 'success',
            text: `Добавлено на проверку цены: ${count}. Открыта очередь «Не проверены → Добавлены вручную». Проверку проводит напарник в обычной карточке.`,
        });
        setManualQueueOpen(false);
        setActiveSection('pricing');
        setPricingFilter('unchecked');
        setSourceFilter('manual');
        setPageNumber(0);
        onDraftChange?.({ catalogWorkspaceTab: 'pricing', catalogPricingFilter: 'unchecked', catalogPageNumber: 0 });
        await onRefresh?.();
        await onRealtimeChange?.({ kind: 'catalog-analysis', categoryId });
    };

    const queueIssueProducts = async (products = []) => {
        const ids = Array.from(new Set((Array.isArray(products) ? products : [])
            .filter((product) => product?._id && (product.modelPath || product.has3D))
            .map((product) => String(product._id)))).slice(0, 50);
        if (!ids.length) {
            showMessage('warning', 'Для отправки на проверку цены нужна загруженная 3D-модель.');
            return;
        }
        setActionLoading('queue-manual');
        try {
            const result = await queueManualReviewProducts(ids, isAllCategories ? '' : categoryId);
            const added = Number(result?.addedCount || 0);
            const linked = (result?.results || []).filter((row) =>
                ['alreadyLinked', 'alreadyQueued'].includes(row.status)).length;
            const errors = (result?.results || []).filter((row) =>
                !['added', 'alreadyLinked', 'alreadyQueued'].includes(row.status));
            const feedback = `В существующую проверку цен добавлено: ${added}. `
                + (linked ? `У ${linked} товаров запись проверки уже есть. ` : '')
                + (errors.length ? `Ошибок: ${errors.length}.` : '');
            showMessage(errors.length ? 'warning' : (added ? 'success' : 'warning'), feedback);
            setDecisionNotice({
                type: errors.length ? 'warning' : 'success',
                text: added
                    ? `${feedback} Открыта вкладка «Цены и подтверждение → Не проверены → Добавлены вручную».`
                    : `${feedback} Новых записей не создано. Проверь фильтр «Все» в разделе цен.`,
            });
            if (added > 0) {
                await onManualQueued();
            } else {
                await loadWorkspace({ silent: true });
            }
        } catch (error) {
            showMessage('error', getErrorMessage(error));
        } finally {
            setActionLoading('');
        }
    };

    const toggleCompare = (productId) => {
        const id = String(productId || '');
        if (!id) return;
        setSelectedCompareIds((previous) => {
            if (previous.includes(id)) return previous.filter((value) => value !== id);
            if (previous.length >= 4) {
                showMessage('warning', 'Для сравнения можно выбрать максимум 4 товара.');
                return previous;
            }
            return [...previous, id];
        });
    };

    const updateNumberExclusion = async (productId, purchasedNumber, excluded) => {
        if (!productId || !purchasedNumber || effectiveLoading) return;
        if (!await askConfirmation({
            title: excluded ? 'Не эта модель?' : 'Вернуть совпадение?',
            description: excluded
                ? `Исключить товар только из совпадений формы №${purchasedNumber}? `
                    + 'Товар останется в каталоге, его 3D и цены не изменятся.'
                : `Снова учитывать этот товар при сопоставлении формы №${purchasedNumber}?`,
            confirmText: excluded ? 'Исключить совпадение' : 'Вернуть совпадение',
            confirmVariant: excluded ? 'danger' : 'primary',
        })) return;
        setActionLoading('relation');
        try {
            const result = await setCatalogReviewNumberExclusion({
                productId, purchasedNumber, excluded,
            });
            // Одноразовая синхронизация очищает застарелые дубли сразу после решения.
            if (result?.categoryId) {
                await analyzeCatalogProductLinks({ categoryId: result.categoryId });
            }
            setSelectedCompareIds((previous) => previous.filter((id) => id !== String(productId)));
            const relationNotice = excluded
                ? `Сохранено: товар больше не относится к форме №${purchasedNumber}. Он не удалён, его цена не изменилась.`
                : `Сохранено: товар снова учитывается для формы №${purchasedNumber}.`;
            showMessage('success', relationNotice);
            setDecisionNotice({ type: 'success', text: relationNotice });
            await loadWorkspace({ silent: true });
            await onRefresh?.();
        } catch (error) {
            showMessage('error', getErrorMessage(error));
        } finally {
            setActionLoading('');
        }
    };

    const changeIssueFilter = (filter) => {
        setIssueFilter(filter);
        setPageNumber(0);
        onDraftChange?.({ catalogPageNumber: 0 });
    };

    const updateSkip = async (productId, ignored, reason = '') => {
        if (!productId || effectiveLoading) return;
        if (ignored && !await askConfirmation({
            title: `${REVIEW_CLASS_LABELS[reason] || 'Отметить товар'}?`,
            description: 'Сохранить классификацию и исключить товар из автоматического сопоставления с купленными формами? '
                + 'Сам товар, его 3D и цена не изменятся. Для передачи напарнику нажми отдельную кнопку «На проверку цены».',
            confirmText: 'Сохранить классификацию',
        })) return;
        setActionLoading('skip');
        try {
            const response = await setCatalogReviewSkip({ productId, ignored, reason });
            setLocalClassifications((previous) => ({
                ...previous,
                [String(productId)]: response?.product?.catalogReviewSkip || { ignored, reason },
            }));
            const stateText = ignored
                ? `${REVIEW_CLASS_LABELS[reason] || 'Товар'}: статус сохранён. Исключён только из автоматического поиска форм; в очередь цены этим действием НЕ отправлен.`
                : 'Статус снят. Товар возвращён в автоматический поиск форм.';
            showMessage('success', stateText);
            setDecisionNotice({ type: 'success', text: stateText + (ignored
                ? ' Для передачи напарнику нажми «На проверку цены» рядом с товаром.'
                : '') });
            await loadWorkspace({ silent: true });
            await onRefresh?.();
        } catch (error) {
            const text = `Не удалось сохранить классификацию: ${getErrorMessage(error)}`;
            showMessage('error', text);
            setDecisionNotice({ type: 'warning', text });
        } finally {
            setActionLoading('');
        }
    };

    const changeSourceFilter = (filter) => {
        if (filter === sourceFilter) return;
        setSourceFilter(filter);
        setPageNumber(0);
        onDraftChange?.({ catalogPageNumber: 0 });
    };

    const selectSection = (section) => {
        if (section === activeSection) return;
        setWorkspaceLoading(true);
        setPageNumber(0);
        setActiveSection(section);
        onDraftChange?.({
            catalogWorkspaceTab: section,
            catalogPageNumber: 0,
        });
    };

    const selectGeometryFilter = (filter) => {
        if (activeSection === 'geometry' && geometryFilter === filter) return;
        setWorkspaceLoading(true);
        setPageNumber(0);
        setGeometryFilter(filter);
        setActiveSection('geometry');
        onDraftChange?.({
            catalogWorkspaceTab: 'geometry',
            catalogGeometryFilter: filter,
            catalogPageNumber: 0,
        });
    };

    const selectPricingFilter = (filter) => {
        if (activeSection === 'pricing' && pricingFilter === filter) return;
        setWorkspaceLoading(true);
        setPageNumber(0);
        setPricingFilter(filter);
        setActiveSection('pricing');
        onDraftChange?.({
            catalogWorkspaceTab: 'pricing',
            catalogPricingFilter: filter,
            catalogPageNumber: 0,
        });
    };

    const changePageNumber = (nextPage) => {
        const normalizedPage = Math.max(
            0,
            Math.floor(Number(nextPage) || 0)
        );

        if (normalizedPage === Number(pageNumber)) return;

        setWorkspaceLoading(true);
        setPageNumber(normalizedPage);
        onDraftChange?.({
            catalogPageNumber: normalizedPage,
        });
    };

    const startUncheckedPricingReview = async () => {
        setActionLoading('prepare-review');
        setMessage(null);

        let allUncheckedRows;
        try {
            allUncheckedRows = await loadAllRowsForAction({
                section: 'pricing',
                pricingFilter: 'unchecked',
            });
        } catch (error) {
            showMessage('error', getErrorMessage(error));
            setActionLoading('');
            return;
        }

        const first = allUncheckedRows[0];
        if (!first) {
            showMessage('success', 'Непроверенных цен больше нет.');
            setActionLoading('');
            return;
        }

        setActionLoading('');
        onOpenPricing?.(first.purchasedNumber, {
            quickQueue: true,
            queueNumbers: allUncheckedRows.map((row) => row.purchasedNumber),
        });
    };

    const remoteAction = remoteAnalyze
        ? 'analyze'
        : (remoteEstimate
            ? 'estimate'
            : (remoteApply ? 'apply' : (remoteReport ? 'report' : '')));
    const visibleAction = actionLoading === 'prepare-review'
        ? remoteAction
        : (actionLoading || remoteAction);
    const actionLabels = {
        analyze: 'Анализируем каталог товаров…',
        'prepare-geometry': 'Подготавливаем очередь 3D-моделей…',
        estimate: 'Рассчитываем отсутствующие цены…',
        'prepare-review': 'Собираем очередь непроверенных цен…',
        'prepare-apply': 'Подготавливаем товары для переноса…',
        apply: 'Переносим подтверждённые цены в товары…',
        report: 'Формируем CSV-отчёт…',
    };
    const actionDescription = visibleAction === 'analyze'
        && effectiveCategoryProgress.total > 0
        ? [
            `${effectiveCategoryProgress.current} из ${effectiveCategoryProgress.total}`,
            effectiveCategoryProgress.name,
        ].filter(Boolean).join(' · ')
        : '';

    return (
        <section
            className="catalogReview__panel catalogReview__extensionPanel catalogReview__unifiedCatalog"
            data-catalog-sync-scope={`extension-${categoryId || 'none'}`}
        >
            <div className="catalogReview__extensionTopbar">
                <div>
                    <span>Купленный каталог №{CATALOG_FROM}–{CATALOG_TO} + ручные товары</span>
                    <small>
                        Для купленного каталога номер берётся из <code>description</code>. Анализ не меняет
                        поля товара, пока ты явно не перенесёшь подтверждённые цены.
                    </small>
                </div>

                <button
                    type="button"
                    className="is-primary"
                    disabled={effectiveLoading || effectiveBatch.running || !categoryId}
                    onClick={analyzeAll}
                >
                    {analyzeRunning ? (
                        <span className="catalogReview__buttonLoading">
                            <i className="catalogReview__buttonSpinner" aria-hidden="true" />
                            {effectiveCategoryProgress.total > 0
                                ? `Анализ ${effectiveCategoryProgress.current}/${effectiveCategoryProgress.total}`
                                : 'Анализ…'}
                        </span>
                    ) : 'Проанализировать заново'}
                </button>
            </div>

            {message ? (
                <div className={`catalogReview__message catalogReview__message--${message.type}`}>
                    {message.text}
                </div>
            ) : null}

            {visibleAction ? (
                <CatalogLoadingState
                    compact
                    title={actionLabels[visibleAction] || 'Выполняем операцию…'}
                    description={actionDescription}
                />
            ) : null}

            {!workspace && workspaceLoading ? (
                <CatalogLoadingState
                    title="Загружаем товары, 3D и цены…"
                    description="Подготавливаем сводку и первые строки выбранной категории."
                />
            ) : workspace ? (
                <div className="catalogReview__extensionWorkspace">
                    <div
                        className="catalogReview__extensionStats catalogReview__unifiedStats"
                        aria-label="Сводка выбранной категории"
                    >
                        {summaryGroups.map((group) => (
                            <article
                                key={group.key}
                                className={`catalogReview__summaryGroup is-${group.key}`}
                            >
                                <span>{group.label}</span>
                                <strong>{group.value}</strong>
                                <small>{group.details.join(' · ')}</small>
                            </article>
                        ))}
                    </div>

                    <AdminSearchFilterPanel
                        title="Поиск по каталогу"
                        summary={catalogSearch
                            ? `Найденное по запросу: ${catalogSearch}`
                            : 'Номер формы, название товара, артикул, категория или тег'}
                        open={catalogSearchPanelOpen}
                        onOpenChange={changeCatalogSearchPanelOpen}
                        searchValue={catalogSearchDraft}
                        onSearchChange={changeCatalogSearchDraft}
                        searchLabel="Поиск"
                        searchPlaceholder="№ формы, название, артикул, категория, тег"
                        disabled={workspaceLoading || !categoryId}
                    />

                    <div className="catalogReview__catalogSections">
                        {SECTIONS.map((section) => (
                            <button
                                type="button"
                                key={section.value}
                                className={activeSection === section.value ? 'is-active' : ''}
                                onClick={() => selectSection(section.value)}
                            >
                                {workspaceLoading && activeSection === section.value ? (
                                    <i className="catalogReview__buttonSpinner" aria-hidden="true" />
                                ) : null}
                                {section.label}
                            </button>
                        ))}
                    </div>

                    <div className="catalogReview__extensionActions catalogReview__contextActions">
                        {(activeSection === 'catalog' || activeSection === 'issues') ? (
                            <div className="catalogReview__compareActions">
                                <button type="button" className="is-compare"
                                    disabled={selectedCompareIds.length < 2}
                                    onClick={() => setCompareIds(selectedCompareIds.slice())}>
                                    Сравнить выбранные ({selectedCompareIds.length}/4)
                                </button>
                                {selectedCompareIds.length ? (
                                    <button type="button" onClick={() => setSelectedCompareIds([])}>Очистить выбор</button>
                                ) : null}
                            </div>
                        ) : null}
                        {['catalog', 'issues'].includes(activeSection) ? (
                            <button type="button" className="is-primary"
                                disabled={effectiveLoading || effectiveBatch.running}
                                onClick={() => setManualQueueOpen(true)}>
                                + Отправить товары с 3D на проверку
                            </button>
                        ) : null}
                        {activeSection === 'catalog' ? (
                            <span>
                                {summary.foundNumbers || 0} с номером
                                {' · '}{summary.withoutNumber || 0} без номера
                                {' · '}{summary.duplicateNumbers || 0} дублей
                            </span>
                        ) : null}

                        {activeSection === 'geometry' ? (
                            <>
                                <div className="catalogReview__primaryActions">
                                    {!effectiveBatch.running ? (
                                        <>
                                            <button
                                                type="button"
                                                className="is-primary"
                                                disabled={!Number(summary.geometryPending || 0) || effectiveLoading}
                                                onClick={analyze3D}
                                            >
                                                {geometryQueueLoading ? (
                                                    <span className="catalogReview__buttonLoading">
                                                        <i className="catalogReview__buttonSpinner" aria-hidden="true" />
                                                        Подготовка…
                                                    </span>
                                                ) : `Получить размеры · ${Number(summary.geometryPending || 0)}` }
                                            </button>
                                            {Number(summary.geometryFailed || 0) ? (
                                                <button
                                                    type="button"
                                                    className="is-warning"
                                                    disabled={effectiveLoading}
                                                    onClick={retryFailed3D}
                                                >
                                                    {geometryQueueLoading ? (
                                                        <span className="catalogReview__buttonLoading">
                                                            <i className="catalogReview__buttonSpinner" aria-hidden="true" />
                                                            Подготовка…
                                                        </span>
                                                    ) : `Повторить ошибки · ${Number(summary.geometryFailed || 0)}` }
                                                </button>
                                            ) : null}
                                        </>
                                    ) : (
                                        <button
                                            type="button"
                                            className="is-danger"
                                            onClick={stopBatch}
                                        >
                                            Остановить после текущей модели
                                        </button>
                                    )}
                                </div>
                                <div className="catalogReview__reviewFilterBar">
                                    <button
                                        type="button"
                                        className={geometryFilter === 'unchecked' ? 'is-active is-unchecked' : ''}
                                        onClick={() => selectGeometryFilter('unchecked')}
                                    >
                                        Ожидают {Number(summary.geometryPending || 0)}
                                    </button>
                                    <button
                                        type="button"
                                        className={geometryFilter === 'measured' ? 'is-active is-checked' : ''}
                                        onClick={() => selectGeometryFilter('measured')}
                                    >
                                        Измерены {Number(summary.geometryMeasured || 0)}
                                    </button>
                                    <button
                                        type="button"
                                        className={geometryFilter === 'failed' ? 'is-active is-error' : ''}
                                        onClick={() => selectGeometryFilter('failed')}
                                    >
                                        Ошибки {Number(summary.geometryFailed || 0)}
                                    </button>
                                    <button
                                        type="button"
                                        className={geometryFilter === 'without3D' ? 'is-active' : ''}
                                        onClick={() => selectGeometryFilter('without3D')}
                                    >
                                        Без 3D {Number(summary.without3D || 0)}
                                    </button>
                                    <button
                                        type="button"
                                        className={geometryFilter === 'all' ? 'is-active' : ''}
                                        onClick={() => selectGeometryFilter('all')}
                                    >
                                        Все {Number(summary.linkedItems || 0)}
                                    </button>
                                </div>
                            </>
                        ) : null}

                        {activeSection === 'pricing' ? (
                            <>
                                <div className="catalogReview__primaryActions">
                                    <button
                                        type="button"
                                        className="is-primary"
                                        disabled={effectiveLoading || effectiveBatch.running}
                                        onClick={recalculatePrices}
                                    >
                                        {estimateRunning ? (
                                            <span className="catalogReview__buttonLoading">
                                                <i className="catalogReview__buttonSpinner" aria-hidden="true" />
                                                Рассчитываем…
                                            </span>
                                        ) : 'Рассчитать отсутствующие цены'}
                                    </button>
                                    <button
                                        type="button"
                                        className="is-review-start"
                                        disabled={!Number(summary.pricingUnchecked || 0) || effectiveLoading || effectiveBatch.running}
                                        onClick={startUncheckedPricingReview}
                                    >
                                        {reviewQueueLoading ? (
                                            <span className="catalogReview__buttonLoading">
                                                <i className="catalogReview__buttonSpinner" aria-hidden="true" />
                                                Подготовка…
                                            </span>
                                        ) : `Начать проверку · ${Number(summary.pricingUnchecked || 0)}` }
                                    </button>
                                    <button
                                        type="button"
                                        className="is-success"
                                        disabled={effectiveLoading || effectiveBatch.running || !summary.readyForImport}
                                        onClick={applyReadyPrices}
                                    >
                                        {applyRunning ? (
                                            <span className="catalogReview__buttonLoading">
                                                <i className="catalogReview__buttonSpinner" aria-hidden="true" />
                                                {actionLoading === 'prepare-apply' ? 'Подготовка…' : 'Переносим…'}
                                            </span>
                                        ) : `Перенести все · ${summary.readyForImport || 0}` }
                                    </button>
                                </div>
                                <div className="catalogReview__manualSourceFilters" aria-label="Источники товаров">
                                    <span>Источник:</span>
                                    {[
                                        { value: 'all', label: 'Все товары' },
                                        { value: 'catalog', label: 'Купленный каталог' },
                                        { value: 'manual', label: 'Добавлены вручную' },
                                    ].map((option) => (
                                        <button key={option.value} type="button"
                                            className={sourceFilter === option.value ? 'is-active' : ''}
                                            onClick={() => changeSourceFilter(option.value)}>
                                            {option.label}
                                        </button>
                                    ))}
                                </div>
                                <div
                                    className="catalogReview__reviewFilterBar"
                                    data-catalog-sync-scope="catalog-pricing-filters"
                                    aria-label="Фильтры цен и переноса"
                                >
                                    <button
                                        type="button"
                                        aria-pressed={pricingFilter === 'notImported'}
                                        className={pricingFilter === 'notImported'
                                            ? 'is-active is-not-imported'
                                            : ''}
                                        onClick={() => selectPricingFilter('notImported')}
                                        title="Подтверждены, но ещё не перенесены или изменены после переноса"
                                    >
                                        Не перенесены {Number(summary.readyForImport || 0)}
                                    </button>
                                    <button
                                        type="button"
                                        aria-pressed={pricingFilter === 'imported'}
                                        className={pricingFilter === 'imported'
                                            ? 'is-active is-imported'
                                            : ''}
                                        onClick={() => selectPricingFilter('imported')}
                                        title="Фрезеровка уже перенесена в товар и после этого не изменялась"
                                    >
                                        Перенесены {Number(summary.importedCount || 0)}
                                    </button>
                                    <button
                                        type="button"
                                        aria-pressed={pricingFilter === 'unchecked'}
                                        className={pricingFilter === 'unchecked' ? 'is-active is-unchecked' : ''}
                                        onClick={() => selectPricingFilter('unchecked')}
                                    >
                                        Не проверены {Number(summary.pricingUnchecked || 0)}
                                    </button>
                                    <button
                                        type="button"
                                        aria-pressed={pricingFilter === 'checked'}
                                        className={pricingFilter === 'checked' ? 'is-active is-checked' : ''}
                                        onClick={() => selectPricingFilter('checked')}
                                    >
                                        Проверены {Number(summary.pricingChecked || 0)}
                                    </button>
                                    <button
                                        type="button"
                                        aria-pressed={pricingFilter === 'all'}
                                        className={pricingFilter === 'all' ? 'is-active' : ''}
                                        onClick={() => selectPricingFilter('all')}
                                    >
                                        Все {Number(summary.pricingUnchecked || 0) + Number(summary.pricingChecked || 0)}
                                    </button>
                                </div>
                            </>
                        ) : null}

                        {activeSection === 'issues' ? (
                            <>
                                <div className="catalogReview__manualSourceFilters" aria-label="Фильтры проблем">
                                    {[
                                        { value: 'all', label: 'Все проблемы' },
                                        { value: 'duplicate', label: 'Дубли' },
                                        { value: 'missing3d', label: 'Без 3D' },
                                        { value: 'geometry', label: 'Ошибка замера' },
                                        { value: 'withoutNumber', label: 'Без номера' },
                                        { value: 'skipped', label: 'Пропущенные' },
                                    ].map((option) => (
                                        <button type="button" key={option.value}
                                            className={issueFilter === option.value ? 'is-active' : ''}
                                            onClick={() => changeIssueFilter(option.value)}>
                                            {option.label}
                                        </button>
                                    ))}
                                </div>
                                <div className="catalogReview__primaryActions">
                                    <button
                                        type="button"
                                        disabled={effectiveLoading || effectiveBatch.running}
                                        onClick={downloadReport}
                                    >
                                        {reportRunning ? (
                                            <span className="catalogReview__buttonLoading">
                                                <i className="catalogReview__buttonSpinner" aria-hidden="true" />
                                                Формируем…
                                            </span>
                                        ) : 'Скачать CSV'}
                                    </button>
                                </div>
                                <span>
                                    Здесь собраны дубли, товары без номера,
                                    модели без 3D и ошибки измерения.
                                </span>
                            </>
                        ) : null}
                    </div>

                    {effectiveBatch.running || effectiveBatch.total > 0 ? (
                        <div className="catalogReview__extensionProgress">
                            <div>
                                <strong>
                                    {effectiveBatch.running
                                        ? `Анализ формы №${effectiveBatch.currentNumber || '—'}`
                                        : 'Последний анализ завершён'}
                                </strong>
                                <span>
                                    {effectiveBatch.current} / {effectiveBatch.total}
                                    {' · '}успешно {effectiveBatch.success}
                                    {' · '}ошибок {effectiveBatch.failed}
                                </span>
                            </div>
                            <progress
                                max={Math.max(1, effectiveBatch.total)}
                                value={Math.min(effectiveBatch.current, effectiveBatch.total)}
                            />
                        </div>
                    ) : null}

                    <div
                        className="catalogReview__extensionTableWrap"
                        data-catalog-scroll-id="catalog-products-table"
                    >
                        <table className="catalogReview__extensionTable">
                            <thead>
                                <tr>
                                    <th>Форма</th>
                                    <th>Фото</th>
                                    <th>Товар</th>
                                    <th>3D и размер</th>
                                    <th>Фрезеровка</th>
                                    <th>Состояние</th>
                                    <th>Действия</th>
                                </tr>
                            </thead>
                            <tbody>
                                {visibleRows.length ? visibleRows.map((row) => {
                                    const item = row.existingItem || {};
                                    const geometry = item.geometryAnalysis || {};
                                    const dimensions = geometry.status === 'measured'
                                        ? geometry.dimensions
                                        : null;
                                    const duplicate3DCount = row.status === 'duplicate'
                                        ? (row.products || []).filter((product) => product.has3D === true).length
                                        : 0;
                                    const dimensionText = row.status === 'duplicate'
                                        ? 'Сначала определите соответствие'
                                        : hasDimensions(dimensions)
                                        ? size(dimensions)
                                        : (
                                            row.has3D
                                                ? 'Нужно получить размер 3D'
                                                : 'Нужна 3D-модель'
                                        );
                                    const estimate = item.estimate || {};
                                    const price = item.pricingDraft?.basePrice || 0;
                                    const imageCount = Array.from(new Set([
                                        row.imagePath,
                                        ...(row.galleryImages || []),
                                        ...(row.product?.galleryImages || []),
                                    ].filter(Boolean))).length;

                                    const reviewed = isReviewedStatus(item.reviewStatus);
                                    const rowClassName = [
                                        reviewed ? 'is-review-checked' : 'is-review-unchecked',
                                        row.geometryStatus ? `is-geometry-${row.geometryStatus}` : '',
                                        row.status === 'duplicate' ? 'is-row-duplicate' : '',
                                    ].filter(Boolean).join(' ');

                                    return (
                                        <tr
                                            key={`${activeSection}-${row._scopeKey || row.purchasedNumber}`}
                                            className={rowClassName}
                                            data-catalog-sync-scope={`catalog-row-${row._scopeKey || row.purchasedNumber}`}
                                        >
                                            <td>
                                                <strong>{item.catalogSource?.type === 'manual-product'
                                                     ? 'Ручной товар'
                                                     : `№${row.purchasedNumber}`}</strong>
                                                {isAllCategories ? (
                                                    <small className="catalogReview__rowCategory">
                                                        {row._scopeCategoryName || 'Категория'}
                                                    </small>
                                                ) : null}
                                                {item.catalogSource?.type === 'manual-product' ? (
                                                    <span className="catalogReview__extensionSource">Добавлен вручную</span>
                                                ) : null}
                                                {item.catalogSource?.type === 'new-catalog-product' ? (
                                                    <span className="catalogReview__extensionSource">
                                                        Новый каталог
                                                    </span>
                                                ) : null}
                                            </td>
                                            <td>
                                                <button
                                                    type="button"
                                                    className="catalogReview__extensionImageButton"
                                                    disabled={!row.imagePath}
                                                    onClick={() => openGallery(row)}
                                                >
                                                    {row.imagePath ? (
                                                        <img
                                                            src={resolveAnyStaticUrl(row.imagePath)}
                                                            alt={item.catalogSource?.type === 'manual-product' ? productName(row.product) : `Форма №${row.purchasedNumber}`}
                                                            loading="lazy"
                                                        />
                                                    ) : (
                                                        <span>Нет фото</span>
                                                    )}
                                                    <small>{imageCount} фото</small>
                                                </button>
                                            </td>
                                            <td>
                                                {row.status === 'duplicate' ? (
                                                    <>
                                                        <strong>
                                                            {row.products?.length || 0} товара
                                                        </strong>
                                                        <details className="catalogReview__duplicateDetails">
                                                            <summary>Показать все совпавшие товары</summary>
                                                            <small>«Это вариант / часть» только исключает товар из автоматического поиска. Для оценки нажми «На проверку цены».</small>
                                                            {(row.products || []).some((candidate) => candidate.has3D && candidate._id) ? (
                                                                <button type="button" className="catalogReview__issueGroupQueue"
                                                                    disabled={effectiveLoading}
                                                                    onClick={() => queueIssueProducts(row.products)}>
                                                                    Отправить все доступные 3D на проверку цен · {(row.products || []).filter((candidate) => candidate.has3D && candidate._id).length}
                                                                </button>
                                                            ) : null}
                                                        {(row.products || [])
                                                            .filter(Boolean)
                                                            .map((rawProduct, index) => {
                                                                const product = {
                                                                    ...rawProduct,
                                                                    catalogReviewSkip: localClassifications[String(rawProduct._id)]
                                                                        || rawProduct.catalogReviewSkip || {},
                                                                };
                                                                const ignored = product.catalogReviewSkip?.ignored === true;
                                                                const queued = product.manualReviewQueued === true;
                                                                return (
                                                                <div className={`catalogReview__candidate ${ignored ? 'is-classified' : ''}`} key={product._id || index}>
                                                                    <input type="checkbox" aria-label={`Выбрать ${productName(product)} для сравнения`}
                                                                        checked={selectedCompareIds.includes(String(product._id))}
                                                                        disabled={!product._id}
                                                                        onChange={() => toggleCompare(product._id)} />
                                                                    <button type="button" className="catalogReview__candidateImage"
                                                                        onClick={() => setCompareIds([String(product._id)])}
                                                                        disabled={!product._id} title="Посмотреть фотографии и карточку">
                                                                        {product.reducedImage || product.galleryImages?.[0]
                                                                            ? <img src={resolveAnyStaticUrl(product.reducedImage || product.galleryImages[0])}
                                                                                alt={productName(product)} loading="lazy" />
                                                                            : <span>Нет фото</span>}
                                                                    </button>
                                                                    <div className="catalogReview__candidateBody">
                                                                        <strong>{productName(product)}</strong>
                                                                        <small>Артикул {product.article || '—'} · {product.has3D ? '3D есть' : 'Без 3D'}</small>
                                                                        <span className={`catalogReview__reviewState ${queued ? 'is-queued' : ''}`}>
                                                                            {priceQueueState(product)}
                                                                        </span>
                                                                        {ignored ? (
                                                                            <span className="catalogReview__classificationState">
                                                                                ✓ {REVIEW_CLASS_LABELS[product.catalogReviewSkip.reason] || 'Пропущено'} · сохранено, исключено из поиска форм
                                                                            </span>
                                                                        ) : null}
                                                                        <span className="catalogReview__issueCandidateActions">
                                                                            <button type="button" disabled={!product._id}
                                                                                onClick={() => setCompareIds([String(product._id)])}>
                                                                                Карточка и фото
                                                                            </button>
                                                                            <button type="button" disabled={!product._id}
                                                                                onClick={() => toggleCompare(product._id)}>
                                                                                {selectedCompareIds.includes(String(product._id)) ? 'Убрать из сравнения' : 'Сравнить'}
                                                                            </button>
                                                                            {activeSection === 'issues' && product._id && product.has3D ? (
                                                                                <button type="button" className="is-primary"
                                                                                    disabled={effectiveLoading || queued || product.catalogReviewLinked === true}
                                                                                    title={queued ? 'Товар уже отправлен на проверку цены'
                                                                                        : 'Поставить этот товар в существующую очередь проверки цены'}
                                                                                    onClick={() => queueIssueProducts([product])}>
                                                                                    {queued ? '✓ В проверке цен' : (product.catalogReviewLinked ? 'Уже есть проверка' : 'На проверку цены')}
                                                                                </button>
                                                                            ) : null}
                                                                            {activeSection === 'issues' && product._id ? (
                                                                                <>
                                                                                    {ignored ? (
                                                                                        <button type="button" className="is-warning"
                                                                                            disabled={effectiveLoading}
                                                                                            onClick={() => updateSkip(product._id, false)}>
                                                                                            Вернуть в поиск форм
                                                                                        </button>
                                                                                    ) : (
                                                                                        <>
                                                                                            <button type="button" disabled={effectiveLoading}
                                                                                                onClick={() => updateSkip(product._id, true, 'variant')}>
                                                                                                Это вариант
                                                                                            </button>
                                                                                            <button type="button" disabled={effectiveLoading}
                                                                                                onClick={() => updateSkip(product._id, true, 'part')}>
                                                                                                Это часть
                                                                                            </button>
                                                                                            <button type="button" disabled={effectiveLoading}
                                                                                                onClick={() => updateSkip(product._id, true, 'handmade')}>
                                                                                                Ручная модель
                                                                                            </button>
                                                                                        </>
                                                                                    )}
                                                                                    <button type="button" className="is-warning" disabled={effectiveLoading}
                                                                                        onClick={() => updateNumberExclusion(product._id, row.purchasedNumber, true)}>
                                                                                        Не эта модель
                                                                                    </button>
                                                                                </>
                                                                            ) : null}
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                                );
                                                            })}
                                                        </details>
                                                    </>
                                                ) : (
                                                    <>
                                                        <strong>{productName(row.product)}</strong>
                                                        {row.product?._id && (activeSection === 'catalog' || activeSection === 'issues') ? (
                                                            <span className="catalogReview__issueCandidateActions">
                                                                <button type="button" onClick={() => setCompareIds([String(row.product._id)])}>
                                                                    Карточка и фото
                                                                </button>
                                                                <button type="button" onClick={() => toggleCompare(row.product._id)}>
                                                                    {selectedCompareIds.includes(String(row.product._id)) ? '✓ В сравнении' : '+ Сравнить'}
                                                                </button>
                                                            </span>
                                                        ) : null}
                                                        <small>
                                                            Артикул: {row.product?.article || '—'}
                                                        </small>
                                                        <small>
                                                            {item.catalogSource?.type === 'manual-product' ? 'Ручная проверка' : 'Номер: description'}
                                                        </small>
                                                    </>
                                                )}
                                            </td>
                                            <td>
                                                <strong>{dimensionText}</strong>
                                                <span className={row.has3D ? 'is-ok' : 'is-muted'}>
                                                    {row.status === 'duplicate'
                                                        ? `3D есть у ${duplicate3DCount} из ${(row.products || []).length}`
                                                        : (row.has3D ? '3D есть' : '3D нет')}
                                                </span>
                                                <small>
                                                    {row.status === 'duplicate'
                                                        ? 'Не выбрано соответствие товара'
                                                        : geometryStatusLabel(row.geometryStatus)}
                                                </small>
                                            </td>
                                            <td>
                                                <strong>{money(price)}</strong>
                                                <small>
                                                    {money(estimate.priceRange?.min)}
                                                    {' — '}
                                                    {money(estimate.priceRange?.max)}
                                                </small>
                                                <span className={`catalogReview__reviewStatusPill ${
                                                    reviewed ? 'is-checked' : 'is-unchecked'
                                                }`}>
                                                    {reviewStatusLabel(item.reviewStatus)}
                                                </span>
                                                {getLastPriceEditFromItem(item)?.userName ? (
                                                    <small className="catalogReview__priceEditor">
                                                        Менял: <b>{getLastPriceEditFromItem(item).userName}</b>
                                                        {formatPriceEditTime(getLastPriceEditFromItem(item).createdAt)
                                                            ? ` · ${formatPriceEditTime(getLastPriceEditFromItem(item).createdAt)}`
                                                            : ''}
                                                    </small>
                                                ) : null}
                                            </td>
                                            <td>
                                                <strong className={`is-${row.status}`}>
                                                    {rowStateLabel(row)}
                                                </strong>
                                                {row.transfer?.status === 'imported' ? (
                                                    <small className="catalogReview__transferState is-imported">
                                                        Перенесено в товар
                                                    </small>
                                                ) : null}
                                                {row.transfer?.status === 'changed' ? (
                                                    <small className="catalogReview__transferState is-changed">
                                                        Есть изменения для переноса
                                                    </small>
                                                ) : null}
                                                {row.transfer?.status === 'pending' ? (
                                                    <small className="catalogReview__transferState is-pending">
                                                        Готово к переносу
                                                    </small>
                                                ) : null}
                                            </td>
                                            <td>
                                                <div className="catalogReview__extensionRowActions">
                                                    <button
                                                        type="button"
                                                        className="is-primary"
                                                        disabled={!item._id}
                                                        onClick={() => onOpenPricing?.(row.purchasedNumber, {
                                                            initialView: 'pricing',
                                                            queueNumbers: (
                                                                activeSection === 'pricing'
                                                                    ? visibleRows
                                                                    : pricingRows
                                                            )
                                                                .filter((candidate) => candidate.existingItem?._id)
                                                                .map((candidate) => candidate.purchasedNumber),
                                                        })}
                                                    >
                                                        Открыть карточку
                                                    </button>
                                                    {activeSection === 'pricing'
                                                        && item.readyForAdminImport === true
                                                        && row.product?._id ? (
                                                        <button
                                                            type="button"
                                                            className="is-success"
                                                            disabled={effectiveLoading || effectiveBatch.running}
                                                            onClick={() => applySinglePrice(row)}
                                                        >
                                                            {singleTransferNumber === row.purchasedNumber
                                                                ? 'Переносим…'
                                                                : 'Перенести'}
                                                        </button>
                                                    ) : null}
                                                    {activeSection === 'pricing'
                                                        && row.transfer?.status === 'imported'
                                                        && row.product?._id ? (
                                                        <button
                                                            type="button"
                                                            className="is-warning"
                                                            disabled={effectiveLoading || effectiveBatch.running}
                                                            onClick={() => revertSinglePrice(row)}
                                                        >
                                                            {singleRevertNumber === row.purchasedNumber
                                                                ? 'Возвращаем…'
                                                                : 'Вернуть назад'}
                                                        </button>
                                                    ) : null}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                }) : (
                                    <tr>
                                        <td colSpan="7">
                                            <div className="catalogReview__extensionEmptyRows">
                                                <strong>В этом разделе пока нет строк</strong>
                                                <span>Запрос завершён. Измени фильтр или выбери другой раздел.</span>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    <PaginationComponent
                        totalAll={Number(workspace.pagination?.total || 0)}
                        pageSize={pageSize}
                        pageNumber={pageNumber}
                        setPageNumber={changePageNumber}
                        totalPages={Math.max(1, Number(workspace.pagination?.totalPages || 1))}
                    />

                    {activeSection === 'issues'
                        && ['all', 'withoutNumber'].includes(issueFilter)
                        && workspace.withoutNumber?.length ? (
                        <details className="catalogReview__extensionMissing" open>
                            <summary>Товары без номера в description: {workspace.withoutNumber.length}</summary>
                            <p>Отсутствие номера не мешает отправить товар с 3D на проверку цены.</p>
                            <div className="catalogReview__noNumberProducts">
                                {workspace.withoutNumber.map((product) => (
                                    <CatalogIssueProductCard key={product._scopeKey || product._id}
                                        product={product} busy={effectiveLoading}
                                        note="Не привязан к номеру купленной формы"
                                        onOpen={(id) => setCompareIds([id])}
                                        onQueue={queueIssueProducts}
                                        actionText="Пропустить поиск купленного каталога"
                                        onAction={(row) => updateSkip(row._id, true, 'handmade')} />
                                ))}
                            </div>
                        </details>
                    ) : null}

                    {activeSection === 'issues' && ['all', 'skipped', 'duplicate'].includes(issueFilter)
                        && workspace.excludedMatches?.length ? (
                        <details className="catalogReview__extensionMissing" open={issueFilter === 'skipped'}>
                            <summary>Исключённые случайные совпадения: {workspace.excludedMatches.length}</summary>
                            <p>Исключена только связь с конкретной формой. Сам товар не удалён и его цена не менялась.</p>
                            <div className="catalogReview__noNumberProducts">
                                {workspace.excludedMatches.map((entry) => (
                                    <CatalogIssueProductCard key={`${entry.productId}-${entry.purchasedNumber}`}
                                        product={{ ...entry, _id: entry.productId,
                                            fullName: entry.name, reducedImage: entry.image }}
                                        busy={effectiveLoading}
                                        note={`Не относится к купленной форме №${entry.purchasedNumber}`}
                                        onOpen={(id) => setCompareIds([id])}
                                        onQueue={queueIssueProducts}
                                        actionText="Вернуть совпадение"
                                        onAction={() => updateNumberExclusion(entry.productId, entry.purchasedNumber, false)} />
                                ))}
                            </div>
                        </details>
                    ) : null}

                    {activeSection === 'issues' && ['all', 'skipped'].includes(issueFilter)
                        && workspace.skippedProducts?.length ? (
                        <details className="catalogReview__extensionMissing" open={issueFilter === 'skipped'}>
                            <summary>Пропущенные товары: {workspace.skippedProducts.length}</summary>
                            <p>Пропущены только при автоматическом поиске купленных форм. На оценку цен эти товары не отправлялись автоматически.</p>
                            <div className="catalogReview__noNumberProducts">
                                {workspace.skippedProducts.map((product) => (
                                    <CatalogIssueProductCard key={product._id}
                                        product={product} busy={effectiveLoading}
                                        note={`Причина: ${{variant: 'Вариант', part: 'Часть модели', handmade: 'Ручная модель', other: 'Пропущено'}[product.catalogReviewSkip?.reason] || 'Пропущено'}`}
                                        onOpen={(id) => setCompareIds([id])}
                                        onQueue={queueIssueProducts}
                                        actionText="Вернуть в поиск форм"
                                        onAction={(row) => updateSkip(row._id, false)} />
                                ))}
                            </div>
                        </details>
                    ) : null}

                    {workspaceLoading ? (
                        <div className="catalogReview__extensionWorkspaceOverlay">
                            <CatalogLoadingState
                                compact
                                title="Обновляем список…"
                                description="Текущие строки остаются на экране до получения новых данных."
                            />
                        </div>
                    ) : null}
                </div>
            ) : (
                <div className="catalogReview__muted catalogReview__catalogStart">
                    Выбери категорию. Затем нажми «Проанализировать заново».
                    Система создаст недостающие записи каталога и свяжет их
                    с существующими товарами.
                </div>
            )}

            {Array.isArray(compareIds) ? (
                <AdminProductCompareModal productIds={compareIds}
                    onClose={() => setCompareIds(null)}
                    onAdd={(id) => {
                        if (compareIds.includes(id) || compareIds.length >= 4) return;
                        const next = [...compareIds, id];
                        setCompareIds(next);
                        setSelectedCompareIds(next);
                    }}
                    onRemove={(id) => {
                        const next = compareIds.filter((value) => value !== id);
                        setCompareIds(next);
                        setSelectedCompareIds(next);
                    }} />
            ) : null}

            {confirmation ? (
                <ModalConfirm show
                    title={confirmation.title}
                    description={confirmation.description}
                    confirmText={confirmation.confirmText || 'Подтвердить'}
                    confirmVariant={confirmation.confirmVariant || 'primary'}
                    cancelText="Отмена"
                    handleClose={() => finishConfirmation(false)}
                    onConfirm={() => finishConfirmation(true)} />
            ) : null}

            {decisionNotice ? (
                <div className={`catalogReview__decisionNotice is-${decisionNotice.type || 'success'}`} role="status" aria-live="polite">
                    <span>{decisionNotice.text}</span>
                    <button type="button" aria-label="Закрыть сообщение" onClick={() => setDecisionNotice(null)}>×</button>
                </div>
            ) : null}

            {manualQueueOpen ? (
                <CatalogManualReviewQueueModal categoryId={isAllCategories ? '' : categoryId}
                    onClose={() => setManualQueueOpen(false)} onQueued={onManualQueued} />
            ) : null}

            {galleryRow ? (
                <GalleryModal
                    row={galleryRow}
                    activeIndex={sharedDraft?.galleryImageIndex || 0}
                    onActiveIndexChange={(index) => onDraftChange?.({
                        galleryPurchasedNumber: galleryRow.purchasedNumber,
                        galleryScopeKey: galleryRow._scopeKey || '',
                        galleryImageIndex: index,
                    })}
                    onClose={closeGallery}
                />
            ) : null}
        </section>
    );
};

export default CatalogExtensionPanel;
